import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Checkbox, FormField, FormHelp, Label, LabeledControl } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

import WizardServerError from '../../../../model/WizardServerError';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import { useSelector } from '../../../../selectors/useSelector';

import { useForm } from '../../../../hooks';

import PostingActions from '../../../../actions/PostingActions';
import actions from '../../../../containers/CreateListingPage/actions';
import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../../components/Wizard/actions';

import { postingConstraints } from '../../../../utils/formValidation';
import propertyUtils from '../../../../utils/propertyUtils';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';

import WizardAlert from '../../../../components/WizardAlert';

import type React from 'react';

const fieldName = 'petsAllowed';

type Props = {
  isRelistingFieldset: boolean;
};

const PetPolicyFormField = ({ isRelistingFieldset = false }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { listingAlias, isMultifamily } = useSelector((state) => state.properties.currentProperty);
  const {
    properties: {
      currentProperty: {
        petsAllowed,
        isArchived,
        isFeed,
        restrictions: { isUpdateListingRestricted },
        selfServeOnboardingStatus: { isSelfServeOnboardingInProgress, isUpdateRestricted: isSSOBUpdateRestricted },
      },
    },
    user: {
      features: { canUpdateListing },
    },
  } = useSelector((state) => state);

  // LGE state
  const [errorDisplay, setErrorDisplay] = useState('');

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      petsAllowed,
    },
    postingConstraints,
  );

  const isInputFormDisabled =
    isFeed ||
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted);

  const fieldSetLabel = isRelistingFieldset ? 'What’s your pet policy?' : 'Pets';

  useEffect(() => {
    if (!isMultifamily && isSaveComplete) {
      save({
        target: {
          name: fieldName,
          // @ts-expect-error
          value: inputs.petsAllowed,
        },
      });
    }
  }, [isSaveComplete]);

  const getUpdatedPetsAllowed = (key: string) => {
    const newPetsAllowed = {
      // @ts-expect-error
      ...inputs.petsAllowed,
    };

    if (key !== 'none') {
      newPetsAllowed.none = false;
    }

    let newAllowedValue = null;

    switch (key) {
      case 'none':
        // deprecated - keep in sync until completely removed
        newPetsAllowed.cats = false;
        newPetsAllowed.largeDogs = false;
        newPetsAllowed.smallDogs = false;

        newPetsAllowed.catsDto = { allowed: false, count: 0 };
        newPetsAllowed.smallDogsDto = { allowed: false, count: 0 };
        newPetsAllowed.largeDogsDto = { allowed: false, count: 0 };

        newPetsAllowed.none = true;
        break;

      case 'smallDogsDto':
        // @ts-expect-error
        newAllowedValue = !inputs.petsAllowed.smallDogs;

        // deprecated - keep in sync until completely removed
        newPetsAllowed.smallDogs = newAllowedValue;

        newPetsAllowed.smallDogsDto = {
          allowed: newAllowedValue,
          count: newAllowedValue ? 1 : 0,
        };
        break;

      case 'largeDogsDto':
        // @ts-expect-error
        newAllowedValue = !inputs.petsAllowed.largeDogs;

        // deprecated - keep in sync until completely removed
        newPetsAllowed.largeDogs = newAllowedValue;

        newPetsAllowed.largeDogsDto = {
          allowed: newAllowedValue,
          count: newAllowedValue ? 1 : 0,
        };
        break;

      case 'catsDto':
        // @ts-expect-error
        newAllowedValue = !inputs.petsAllowed.cats;

        // deprecated - keep in sync until completely removed
        newPetsAllowed.cats = newAllowedValue;

        newPetsAllowed.catsDto = {
          allowed: newAllowedValue,
          count: newAllowedValue ? 1 : 0,
        };
        break;

      default:
        break;
    }

    let foundTrue = false;
    for (const [key, value] of Object.entries(newPetsAllowed)) {
      if (key !== 'none' && value === true) {
        foundTrue = true;
      }
    }

    if (!foundTrue) {
      newPetsAllowed.none = true;
    }

    return newPetsAllowed;
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    // @ts-expect-error value as CurrentPropertyProps['petsAllowed']
    const isPetsAllowedEmpty = isInModalState && propertyUtils.getIsPetsAllowedEmpty(value);
    const petsAllowedErrorDisplay = isPetsAllowedEmpty ? 'Enter your pet policy.' : '';
    setErrorDisplay(petsAllowedErrorDisplay);

    if (petsAllowedErrorDisplay || hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const dataToSave = {
      // @ts-expect-error value as CurrentPropertyProps['petsAllowed']
      ...value,
      propertyId: listingAlias,
    };

    // we do not need to pass none when saving
    delete dataToSave.none;

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });
    dispatch(PostingActions.property.pets(dataToSave))
      // @ts-expect-error
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ petsAllowed: value }));
        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      // @ts-expect-error
      .catch((message) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    const { name } = event.target;
    const newPetsAllowed = getUpdatedPetsAllowed(name);

    const mockEvent = {
      target: {
        name: fieldName,
        value: newPetsAllowed,
      },
    };

    clearServerError();
    handleFormChange(mockEvent);
    // @ts-expect-error
    saveDebounced(mockEvent);
  };

  return (
    <div data-testid="PetPolicyFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FormField
        disabled={isInputFormDisabled}
        label={
          <Label required={!isMultifamily && !isInputFormDisabled}>{isMultifamily ? 'Allowed' : fieldSetLabel}</Label>
        }
        error={!!errorDisplay}
        description={<FormHelp error={!!errorDisplay}>{errorDisplay}</FormHelp>}
      >
        <LabeledControl
          label={<Label>No pets allowed</Label>}
          // @ts-expect-error
          control={<Checkbox name="none" checked={inputs.petsAllowed.none === true} onChange={handleChange} />}
        />
        <LabeledControl
          label={<Label>Cats allowed</Label>}
          control={
            // @ts-expect-error
            <Checkbox name="catsDto" checked={inputs.petsAllowed.catsDto.allowed === true} onChange={handleChange} />
          }
        />
        <LabeledControl
          label={<Label>Small dogs allowed</Label>}
          control={
            <Checkbox
              name="smallDogsDto"
              // @ts-expect-error
              checked={inputs.petsAllowed.smallDogsDto.allowed === true}
              onChange={handleChange}
            />
          }
        />
        <LabeledControl
          label={<Label>Large dogs allowed</Label>}
          control={
            <Checkbox
              name="largeDogsDto"
              // @ts-expect-error
              checked={inputs.petsAllowed.largeDogsDto.allowed === true}
              onChange={handleChange}
            />
          }
        />
      </FormField>
    </div>
  );
};

export default PetPolicyFormField;
